import { createEffect, createRoot, For, on, onCleanup } from "solid-js"
import { createStore } from "solid-js/store"
import { Icon } from "@opencode/ui/icon"
import { Toast, toaster } from "@opencode/ui/toast"
import { useLanguage } from "@/runtime/i18n/language"

export type Upload = {
  id: string
  filename: string
  mime: string
  size: number
  loaded: number
  owner?: object
  status?: "uploading" | "failed" | "saved" | "other-project"
  retry?: () => void
  cancel: () => void
}

// Uploads outlive the composer that started them, so one process-wide list feeds every chip
// and the single progress toast.
const [state, setState] = createStore<{ items: Upload[] }>({ items: [] })

export const uploads = {
  items: () => state.items,
  /** Runs `work` while the upload is listed. Resolves to undefined when the user cancels it. */
  async track<T>(
    input: Pick<Upload, "id" | "filename" | "mime" | "size" | "owner">,
    work: (report: (loaded: number) => void, signal: AbortSignal) => Promise<T>,
    options?: { retainOnError: (error: unknown) => void; onCancel?: () => void },
  ): Promise<T | undefined> {
    const controller = new AbortController()
    return new Promise<T | undefined>((resolve, reject) => {
      let running = false
      let settled = false
      const finish = () => {
        settled = true
        setState("items", (items) => items.filter((item) => item.id !== input.id))
      }
      const attempt = () => {
        if (running || settled) return
        running = true
        setState("items", (item) => item.id === input.id, { status: "uploading", loaded: 0 })
        void Promise.resolve()
          .then(() => {
            if (controller.signal.aborted) return undefined
            return work((loaded) => {
              if (!settled) setState("items", (item) => item.id === input.id, "loaded", loaded)
            }, controller.signal)
          })
          .then(
            (result) => {
              if (settled) return
              finish()
              resolve(result)
            },
            (error: unknown) => {
              if (settled) return
              running = false
              if (options) {
                setState("items", (item) => item.id === input.id, "status", "failed")
                options.retainOnError(error)
                return
              }
              finish()
              reject(error)
            },
          )
      }
      setState("items", (items) => [
        ...items,
        {
          ...input,
          loaded: 0,
          status: "uploading",
          retry: options ? attempt : undefined,
          cancel: () => {
            if (settled) return
            controller.abort()
            options?.onCancel?.()
            finish()
            resolve(undefined)
          },
        },
      ])
      attempt()
    })
  },
}

// Sonner builds toast content outside the app's Solid tree: no context and no owner. This host
// lives inside the providers, lends the toast its language instance, and gives the content a
// root of its own so progress stays reactive.
export function UploadToastHost() {
  const language = useLanguage()
  let active: { id: number; dispose: () => void } | undefined
  const dismiss = () => {
    if (!active) return
    toaster.dismiss(active.id)
    active.dispose()
    active = undefined
  }
  createEffect(
    on(
      () => state.items.length > 0,
      (uploading) => {
        if (!uploading) return dismiss()
        if (active) return
        const id = toaster.show(
          (props) =>
            createRoot((dispose) => {
              active = { id: props.toastId, dispose }
              return <UploadToast toastId={props.toastId} language={language} />
            }),
          { persistent: true, resize: () => state.items.length },
        )
        active ??= { id, dispose: () => {} }
      },
    ),
  )
  onCleanup(dismiss)
  return null
}

function UploadToast(props: { toastId: number; language: ReturnType<typeof useLanguage> }) {
  const percent = (item: Upload) => (item.size === 0 ? 100 : Math.floor((item.loaded / item.size) * 100))
  return (
    <Toast toastId={props.toastId}>
      <Toast.Content>
        <For each={state.items}>
          {(item) => (
            <div data-component="upload-row">
              <div data-slot="upload-row-label">
                <span data-slot="upload-row-name" title={item.filename}>
                  {item.filename}
                </span>
                <span data-slot="upload-row-percent">
                  {item.status === "failed"
                    ? props.language.t("prompt.toast.uploading.failed")
                    : props.language.t("prompt.toast.uploading.percent", { percent: percent(item) })}
                </span>
              </div>
              <div data-slot="upload-row-track">
                {item.status === "failed" && (
                  <button type="button" data-slot="upload-row-retry" onClick={() => item.retry?.()}>
                    {props.language.t("prompt.toast.uploading.retry")}
                  </button>
                )}
                <div
                  data-component="upload-progress"
                  role="progressbar"
                  aria-label={item.filename}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={percent(item)}
                >
                  <div data-slot="upload-progress-bar" style={{ width: `${percent(item)}%` }} />
                </div>
                <button
                  type="button"
                  data-slot="upload-row-cancel"
                  aria-label={props.language.t("prompt.toast.uploading.cancel")}
                  onClick={() => item.cancel()}
                >
                  <Icon name="outline-xmark" />
                </button>
              </div>
            </div>
          )}
        </For>
      </Toast.Content>
    </Toast>
  )
}
